import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, Coffee } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const empty = { name: "", role: "", bio: "", image_path: "", active: true, breaks: [], service_assignments: [] };

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CrewPanel() {
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const [crewRes, moduleRes] = await Promise.all([api.get("/admin/crew"), api.get("/admin/modules")]);
    setItems(crewRes.data || []);
    // Commercial pre-configuration: crew coverage may be prepared for any catalog module.
    // Alerts/operations remain inactive until the module is entitled and enabled.
    setModules(moduleRes.data?.items || []);
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.path }));
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Photo upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        image_path: form.image_path || null,
        breaks: (form.breaks || []).filter((b) => b.start_time && b.end_time).map((b) => ({
          weekday: Number(b.weekday) || 0,
          start_time: b.start_time,
          end_time: b.end_time,
        })),
        service_assignments: (form.service_assignments || [])
          .filter((row) => row.module_id)
          .map((row) => ({ module_id: row.module_id, title: (row.title || "").trim() })),
      };
      if (form.id) await api.put(`/admin/crew/${form.id}`, payload);
      else await api.post("/admin/crew", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Could not save crew"); }
  };

  const remove = async (c) => { if (!window.confirm(`Remove ${c.name}?`)) return; await api.delete(`/admin/crew/${c.id}`); load(); };

  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (c) => setForm({ ...empty, ...c, breaks: c.breaks || [], service_assignments: c.service_assignments || [] }) & setOpen(true);

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/crew/${c.id}`, { ...c, active: !c.active, image_path: c.image_path || null });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Could not update crew"); }
  };

  const addBreak = () => setForm((f) => ({ ...f, breaks: [...(f.breaks || []), { weekday: 0, start_time: "12:00", end_time: "13:00" }] }));
  const setBreak = (i, patch) => setForm((f) => ({ ...f, breaks: f.breaks.map((b, idx) => idx === i ? { ...b, ...patch } : b) }));
  const removeBreak = (i) => setForm((f) => ({ ...f, breaks: f.breaks.filter((_, idx) => idx !== i) }));

  const hasService = (moduleId) => (form.service_assignments || []).some((row) => row.module_id === moduleId);
  const toggleService = (moduleId, checked) => setForm((f) => ({
    ...f,
    service_assignments: checked
      ? [...(f.service_assignments || []).filter((row) => row.module_id !== moduleId), { module_id: moduleId, title: "" }]
      : (f.service_assignments || []).filter((row) => row.module_id !== moduleId),
  }));
  const setServiceTitle = (moduleId, title) => setForm((f) => ({
    ...f,
    service_assignments: (f.service_assignments || []).map((row) => row.module_id === moduleId ? { ...row, title } : row),
  }));

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div><h2 className="font-display text-3xl">Crew</h2><p className="text-sm text-muted-foreground mt-1">People, service coverage, dedicated titles, availability & breaks.</p></div>
        <Button onClick={openNew} className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-crew-btn"><Plus className="h-4 w-4 mr-2" /> Add crew</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="crew-list">
        {items.map((c) => (
          <div key={c.id} className={`bg-card border border-border overflow-hidden ${!c.active ? "opacity-50" : ""}`} data-testid={`admin-crew-${c.id}`}>
            <div className="aspect-[3/4] bg-muted overflow-hidden">
              {c.image_path ? <img src={fileUrl(c.image_path)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">No photo</div>}
            </div>
            <div className="p-4">
              <div className="font-display text-lg">{c.name}</div>
              <div className="overline text-primary/50">{c.role}</div>
              <div className="flex flex-wrap gap-1 mt-2">
                <Badge variant={c.active ? "default" : "outline"} className={`text-[10px] ${c.active ? "bg-primary" : ""}`}>{c.active ? "Active" : "Inactive"}</Badge>
                {(c.breaks || []).length > 0 && <Badge variant="outline" className="text-[10px]"><Coffee className="h-2.5 w-2.5 mr-1" />{c.breaks.length} break{c.breaks.length !== 1 ? "s" : ""}</Badge>}
              </div>
              {(c.service_assignments || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(c.service_assignments || []).map((row) => {
                    const mod = modules.find((m) => m.id === row.module_id);
                    return <Badge key={row.module_id} variant="outline" className="text-[10px]">{mod?.name || row.module_id}{row.title ? ` · ${row.title}` : ""}</Badge>;
                  })}
                </div>
              )}
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-crew-${c.id}`}><Pencil className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => toggleActive(c)} data-testid={`toggle-crew-${c.id}`}>{c.active ? "Deactivate" : "Activate"}</Button>
                <Button size="sm" variant="outline" onClick={() => remove(c)} className="text-destructive border-destructive/40" data-testid={`delete-crew-${c.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-muted-foreground text-sm">No crew yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crew-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Edit" : "Add"} crew</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="overline text-primary/60">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cf-name" className="mt-2" /></div>
            <div><Label className="overline text-primary/60">Role</Label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="cf-role" className="mt-2" /></div>
            <div><Label className="overline text-primary/60">Bio</Label>
              <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="cf-bio" className="mt-2" /></div>
            <div>
              <Label className="overline text-primary/60">Photo</Label>
              <div className="flex items-center gap-4 mt-2">
                {form.image_path && <img src={fileUrl(form.image_path)} alt="" className="w-20 h-20 object-cover border border-border" />}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer text-sm hover:border-primary">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="cf-image" />
                </label>
              </div>
            </div>
            <div className="border border-border bg-muted/20 p-4">
              <Label className="overline text-primary/60">Services this crew member can cover</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">Select one or more modules. Each selected module can have its own dedicated title. Unlicensed modules can be preconfigured for future demos or purchase.</p>
              <div className="space-y-2">
                {modules.length === 0 && <p className="text-xs text-muted-foreground italic">No modules are available in the platform catalog yet.</p>}
                {modules.map((m) => {
                  const checked = hasService(m.id);
                  const assignment = (form.service_assignments || []).find((row) => row.module_id === m.id);
                  return (
                    <div key={m.id} className={`border p-3 ${checked ? "border-accent/50 bg-accent/5" : "border-border bg-card"}`}>
                      <label className="flex items-center gap-3 text-sm cursor-pointer">
                        <input type="checkbox" checked={checked} onChange={(e) => toggleService(m.id, e.target.checked)} className="h-4 w-4" data-testid={`cf-service-${m.id}`} />
                        <span className="font-medium flex-1">{m.name}</span>
                        {m.entitlement === "demo" && <Badge variant="outline" className="text-[9px]">Demo</Badge>}
                      </label>
                      {checked && (
                        <div className="mt-3 pl-7">
                          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Title for this service</Label>
                          <Input
                            value={assignment?.title || ""}
                            onChange={(e) => setServiceTitle(m.id, e.target.value)}
                            placeholder={m.id === "spa" ? "e.g. Senior Spa Therapist" : m.id === "yoga" ? "e.g. Yoga Instructor" : m.id === "personal_training" ? "e.g. Personal Trainer" : "e.g. Service Specialist"}
                            className="mt-1"
                            data-testid={`cf-service-title-${m.id}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="cf-active" />
              <Label>Active (available for assigned services)</Label>
            </div>

            <div className="border-t border-border pt-4">
              <div className="flex justify-between items-center mb-2">
                <Label className="overline text-primary/60 flex items-center gap-2"><Coffee className="h-3 w-3" /> Break times</Label>
                <Button size="sm" variant="outline" onClick={addBreak} data-testid="cf-add-break"><Plus className="h-3 w-3 mr-1" />Add break</Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">During these times, this crew cannot be booked.</p>
              <div className="space-y-2">
                {(form.breaks || []).length === 0 && <p className="text-xs text-muted-foreground italic">No breaks configured.</p>}
                {(form.breaks || []).map((b, i) => (
                  // Stable per-row key so React doesn't recycle the row on
                  // reorder / delete. Weekdays use the day name as a stable key.
                  <div key={b._k || (b._k = `brk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)} className="grid grid-cols-[110px_1fr_1fr_auto] gap-2 items-center" data-testid={`cf-break-${i}`}>
                    <select value={b.weekday} onChange={(e) => setBreak(i, { weekday: Number(e.target.value) })} className="border border-border bg-background px-2 h-10 text-sm">
                      {WEEKDAYS.map((d, idx) => <option key={d} value={idx}>{d}</option>)}
                    </select>
                    <Input type="time" value={b.start_time} onChange={(e) => setBreak(i, { start_time: e.target.value })} />
                    <Input type="time" value={b.end_time} onChange={(e) => setBreak(i, { end_time: e.target.value })} />
                    <Button size="sm" variant="outline" onClick={() => removeBreak(i)} className="text-destructive border-destructive/40"><X className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="cf-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
